import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { triggerRefresh } from "@/hooks/useRefresh";
import { peso, num } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export function ReorderDialog({ open, onOpenChange, suppliers }) {
  const { writableBranches, selected } = useBranch();
  const [branchId, setBranchId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const def = writableBranches.some((b) => b.branch_id === selected) ? selected : writableBranches[0]?.branch_id || "";
    setBranchId(def);
    setSupplierId("");
  }, [open, selected, writableBranches]);

  useEffect(() => {
    if (!open || !branchId) return;
    api.get("/reorder/suggestions", { params: { branch_id: branchId } })
      .then((r) => setRows(r.data.map((x) => ({ ...x, checked: true, order_qty: String(x.suggested_qty) }))))
      .catch((e) => toast.error(apiError(e)));
  }, [open, branchId]);

  const setRow = (i, patch) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    setRows(next);
  };

  const picked = rows.filter((r) => r.checked && parseFloat(r.order_qty) > 0);
  const estTotal = picked.reduce((s, r) => s + parseFloat(r.order_qty || 0) * r.unit_cost, 0);

  const createPO = async () => {
    setLoading(true);
    try {
      await api.post("/purchase-orders", {
        branch_id: branchId, supplier_id: supplierId, notes: "Auto-generated from reorder suggestions",
        lines: picked.map((r) => ({
          item_id: r.item_id, qty_ordered: parseFloat(r.order_qty),
          unit_cost: r.unit_cost, vat_inclusive: r.vat_inclusive,
        })),
      });
      toast.success(`Draft PO created with ${picked.length} items`);
      onOpenChange(false);
      triggerRefresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reorder-dialog">
        <DialogHeader><DialogTitle>Reorder suggestions</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Items below reorder point. Suggested qty tops stock up to 2× the reorder point.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Branch</Label>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger data-testid="reorder-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>{writableBranches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="reorder-supplier-select"><SelectValue placeholder="Supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="reorder-empty">Nothing below reorder point at this branch 🎉</p>
        ) : (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr className="border-b">
                  <th className="py-2 px-2"></th><th className="py-2 px-2">Item</th>
                  <th className="py-2 px-2 text-right">On hand</th><th className="py-2 px-2 text-right">Reorder pt</th>
                  <th className="py-2 px-2">Status</th><th className="py-2 px-2 text-right">Order qty</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.item_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`reorder-row-${r.item_id}`}>
                    <td className="py-1.5 px-2">
                      <Checkbox checked={r.checked} onCheckedChange={(c) => setRow(i, { checked: !!c })} data-testid={`reorder-check-${r.item_id}`} />
                    </td>
                    <td className="py-1.5 px-2 font-medium">{r.item_name}</td>
                    <td className="py-1.5 px-2 text-right">{num(r.qty)} {r.unit}</td>
                    <td className="py-1.5 px-2 text-right">{num(r.reorder_point)}</td>
                    <td className="py-1.5 px-2">
                      <Badge className={r.status === "out" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>
                        {r.status === "out" ? "Out" : "Low"}
                      </Badge>
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Input type="number" step="any" min="0" className="w-20 h-8 ml-auto" value={r.order_qty}
                        onChange={(e) => setRow(i, { order_qty: e.target.value })} data-testid={`reorder-qty-${r.item_id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="text-sm text-muted-foreground">{picked.length} items · est. {peso(estTotal)}</div>
          <Button onClick={createPO} disabled={loading || !supplierId || picked.length === 0} data-testid="reorder-create-po-btn">
            {loading ? "Creating…" : "Create draft PO"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
